import { z } from 'zod';
import { IsoTimestamp, UserId } from './common';

/**
 * CandidateProfile captures the structured, LLM-extracted view of a job
 * applicant — their skills, level, years of experience, etc. Distinct from:
 *
 *  - UserProfile  (auth/identity row keyed by Cognito sub — see schemas/user.ts)
 *  - Preferences  (search/filter knobs the user controls directly)
 *  - AnswerBank   (pre-canned answers for application forms)
 *  - Resume       (the raw PDF + bytes)
 *
 * It's populated by the resume-parser pipeline and editable by the user via
 * the mobile / desktop UI. The job-evaluator LLM consults it when ranking
 * job postings against the user's actual experience.
 */

const SENIORITY_LEVEL_VALUES = [
  'IC_junior',
  'IC_mid',
  'IC_senior',
  'IC_staff',
  'IC_principal',
  'manager',
  'senior_manager',
  'director',
  'senior_director',
  'vp',
  'svp',
  'cxo',
] as const;
export const SeniorityLevel = z.enum(SENIORITY_LEVEL_VALUES);
export type SeniorityLevel = z.infer<typeof SeniorityLevel>;

/** Forward-compat read variant — see #482. */
export const SeniorityLevelReadable = z
  .enum([...SENIORITY_LEVEL_VALUES, 'unknown'] as const)
  .catch('unknown');
export type SeniorityLevelReadable = z.infer<typeof SeniorityLevelReadable>;

const TRACK_TYPE_VALUES = ['individual_contributor', 'people_manager', 'either'] as const;
export const TrackType = z.enum(TRACK_TYPE_VALUES);
export type TrackType = z.infer<typeof TrackType>;

/** Forward-compat read variant — see #482. */
export const TrackTypeReadable = z
  .enum([...TRACK_TYPE_VALUES, 'unknown'] as const)
  .catch('unknown');
export type TrackTypeReadable = z.infer<typeof TrackTypeReadable>;

/**
 * Work-authorization status. Captures both common LinkedIn questions in a
 * single enum:
 *  - "Authorized to work in the US?"   yes for citizen/PR/visa_no_sponsorship
 *  - "Will you require sponsorship?"   yes only for requires_sponsorship
 *
 * The applicator maps to the right answer per question, so the user only
 * configures this once.
 */
const WORK_AUTHORIZATION_VALUES = [
  'us_citizen',
  'permanent_resident',
  'visa_no_sponsorship',
  'requires_sponsorship',
] as const;
export const WorkAuthorization = z.enum(WORK_AUTHORIZATION_VALUES);
export type WorkAuthorization = z.infer<typeof WorkAuthorization>;

/** Forward-compat read variant — see #482. */
export const WorkAuthorizationReadable = z
  .enum([...WORK_AUTHORIZATION_VALUES, 'unknown'] as const)
  .catch('unknown');
export type WorkAuthorizationReadable = z.infer<typeof WorkAuthorizationReadable>;

/**
 * Highest education level. Quick single-select for the Profile pane.
 * Detailed `education` entries (school, degree, year) come from the
 * resume parser and stay in the `education` array below.
 */
const EDUCATION_LEVEL_VALUES = [
  'high_school',
  'associate',
  'bachelor',
  'master',
  'doctorate',
  'professional', // JD, MD, etc.
  'other',
] as const;
export const EducationLevel = z.enum(EDUCATION_LEVEL_VALUES);
export type EducationLevel = z.infer<typeof EducationLevel>;

/** Forward-compat read variant — see #482. */
export const EducationLevelReadable = z
  .enum([...EDUCATION_LEVEL_VALUES, 'unknown'] as const)
  .catch('unknown');
export type EducationLevelReadable = z.infer<typeof EducationLevelReadable>;

// Forward-compat read variant — inline enums widened via `.catch('unknown')`.
const SKILL_CATEGORY_VALUES = [
  'language',
  'framework',
  'cloud',
  'database',
  'devops',
  'testing',
  'ai_ml',
  'security',
  'methodology',
  'soft',
  'other',
] as const;
const SkillCategoryReadable = z
  .enum([...SKILL_CATEGORY_VALUES, 'unknown'] as const)
  .catch('unknown');

export const Skill = z.object({
  name: z.string().min(1),
  yearsExperience: z.number().nonnegative().optional(),
  lastUsedYear: z.number().int().min(1980).max(2100).optional(),
  category: SkillCategoryReadable.optional(),
});
export type Skill = z.infer<typeof Skill>;

const LANGUAGE_PROFICIENCY_VALUES = ['basic', 'conversational', 'fluent', 'native'] as const;
const LanguageProficiencyReadable = z
  .enum([...LANGUAGE_PROFICIENCY_VALUES, 'unknown'] as const)
  .catch('unknown');

export const Language = z.object({
  name: z.string().min(1),
  proficiency: LanguageProficiencyReadable,
});
export type Language = z.infer<typeof Language>;

// Strict counterparts for the WRITE path (UpdateCandidateProfileRequest).
const SkillStrict = z.object({
  name: z.string().min(1),
  yearsExperience: z.number().nonnegative().optional(),
  lastUsedYear: z.number().int().min(1980).max(2100).optional(),
  category: z.enum(SKILL_CATEGORY_VALUES).optional(),
});
const LanguageStrict = z.object({
  name: z.string().min(1),
  proficiency: z.enum(LANGUAGE_PROFICIENCY_VALUES),
});

export const WorkHistoryEntry = z.object({
  company: z.string().min(1),
  title: z.string().min(1),
  // #742/#748: optional + `.catch(undefined)`. The LLM resume-extractor
  // returns workHistory items that can either (a) omit the date, or
  // (b) include a string that isn't YYYY-MM (e.g. "Present" for the
  // current job, year-only "2015" for older entries, "Jan 2020", etc.).
  // Both cases must coerce to `undefined` individually — otherwise a
  // single bad value sinks the whole extraction via `runTouchpoint`'s
  // schema-fail branch, and the user sees "parse failed" with no
  // partial result (see CloudWatch req
  // 138857af-6dc8-401a-b546-4b189d3d1c92 for the production repro).
  // Format regex still enforced when the input is valid.
  // Downstream non-test consumers already handle `?? '?'`
  // (see `apps/legacy/src/applicator/form-recovery.ts:77`).
  startDate: z
    .string()
    .regex(/^\d{4}-\d{2}$/)
    .optional()
    .catch(undefined), // YYYY-MM
  endDate: z
    .string()
    .regex(/^\d{4}-\d{2}$/)
    .optional()
    .catch(undefined),
  current: z.boolean().default(false),
  teamSize: z.number().int().nonnegative().optional(),
  highlights: z.array(z.string()).default([]),
});
export type WorkHistoryEntry = z.infer<typeof WorkHistoryEntry>;

export const EducationEntry = z.object({
  degree: z.string().min(1),
  field: z.string().optional(),
  school: z.string().min(1),
  // #748 — same `.catch(undefined)` pattern as workHistory dates. LLM
  // may emit a stringified year ("2010") or an out-of-range value;
  // coerce rather than sink the whole extraction.
  graduationYear: z.number().int().min(1950).max(2100).optional().catch(undefined),
});
export type EducationEntry = z.infer<typeof EducationEntry>;

export const CandidateProfile = z.object({
  userId: UserId,

  // Identity / contact (informational mirror; canonical is User entity)
  fullName: z.string().optional(),

  // Leveling + track
  seniority: SeniorityLevelReadable.optional(),
  track: TrackTypeReadable.default('either'),
  yearsTotal: z.number().nonnegative().optional(),
  // Manager-only fields. The Profile pane hides these unless the user
  // marks themselves as people-manager track or a mgmt-level seniority.
  yearsInLeadership: z.number().nonnegative().optional(),
  largestTeamManaged: z.number().int().nonnegative().optional(),

  // Function / industry the candidate operates in. Free-form strings so
  // the user can drop in whatever palette matches their world (e.g.
  // "Product design", "Quant trading", "Clinical operations"). The
  // job-evaluator LLM uses these as a soft filter on the matched roles.
  industries: z.array(z.string()).default([]),

  // Work-authorization status. Drives the Easy Apply applicator's
  // answer to both the "authorized to work" and "requires sponsorship"
  // gating questions. Optional — when unset the worker falls back to
  // the answer bank.
  workAuthorization: WorkAuthorizationReadable.optional(),

  // Quick education-level select. The structured `education` array below
  // is populated by the resume parser and represents the full history.
  highestEducationLevel: EducationLevelReadable.optional(),

  // Free-form plain-English summary of what the user actually wants out
  // of a job search. Examples:
  //   "Focused on mgmt roles but will accept IC if base > $250k."
  //   "Open to relocation for tier-1 cities; remote-only otherwise."
  //   "Avoid pre-seed startups — need stable W2 for visa."
  // The job-evaluator LLM gets this verbatim alongside the structured
  // prefs, so it can apply nuance the form fields can't capture.
  summary: z.string().max(2000).optional(),

  // Skills / abilities
  skills: z.array(Skill).default([]),
  languages: z.array(Language).default([]),
  certifications: z.array(z.string()).default([]),

  // History
  workHistory: z.array(WorkHistoryEntry).default([]),
  education: z.array(EducationEntry).default([]),

  // Provenance
  sourceResumeVersion: z.number().int().nonnegative().optional(),
  extractedAt: IsoTimestamp.optional(),
  manuallyEditedAt: IsoTimestamp.optional(),

  updatedAt: IsoTimestamp,
});
export type CandidateProfile = z.infer<typeof CandidateProfile>;

/**
 * PATCH body — every widened read field is overridden back to its strict
 * enum so a client can't smuggle 'unknown' into DDB (#482). Same shape
 * as CandidateProfile for the omitted fields, but the enums are strict.
 */
export const UpdateCandidateProfileRequest = CandidateProfile.omit({
  userId: true,
  updatedAt: true,
  extractedAt: true,
})
  .extend({
    seniority: SeniorityLevel,
    track: TrackType,
    workAuthorization: WorkAuthorization,
    highestEducationLevel: EducationLevel,
    skills: z.array(SkillStrict),
    languages: z.array(LanguageStrict),
  })
  .partial();
export type UpdateCandidateProfileRequest = z.infer<typeof UpdateCandidateProfileRequest>;
